/** How a markdown link target should render: inline image preview, inline PDF preview, or a plain
 * (confirm-before-open) external link. */
export type MarkdownMediaKind = 'image' | 'pdf' | 'other';

/** Pure URL classification for markdown links. Signed S3 URLs carry a `?X-Amz-...` query, so the
 * extension is read from the pathname only. */
export interface IMarkdownLinkTarget {
  path: string;
  companyId: number | null;
}

export class MarkdownLinkHelper {
  private static readonly IMAGE_EXTENSIONS = [
    'png',
    'jpg',
    'jpeg',
    'gif',
    'webp',
    'avif',
    'bmp',
  ];

  /** Base for resolving relative hrefs. `.invalid` is reserved, so no real link resolves to this
   * host and seeing it back means the href carried no origin of its own. */
  private static readonly RELATIVE_HREF_ORIGIN = 'https://commenda.invalid';

  private static readonly RELATIVE_HREF_HOSTNAME = 'commenda.invalid';

  private static readonly FIRST_PARTY_DOMAIN = 'commenda.io';

  private static readonly DEV_HOSTNAMES = ['localhost', '127.0.0.1'];

  /** Resolve the pathname, tolerating relative hrefs (resolved against a dummy origin). */
  static getPathname(href: string): string {
    try {
      return new URL(href, MarkdownLinkHelper.RELATIVE_HREF_ORIGIN).pathname;
    } catch {
      return href;
    }
  }

  /** Whether the target is Commenda-owned: a relative href, or an http(s) URL on `commenda.io` or
   * one of its subdomains. Protocol-relative hrefs (`//host/path`) resolve to their own host, so
   * they count as first-party only when that host is ours. */
  static isFirstParty(href: string): boolean {
    let url: URL;
    try {
      url = new URL(href, MarkdownLinkHelper.RELATIVE_HREF_ORIGIN);
    } catch {
      return false;
    }
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
    const hostname = url.hostname.toLowerCase();
    if (hostname === MarkdownLinkHelper.RELATIVE_HREF_HOSTNAME) return true;
    if (
      process.env.NEXT_PUBLIC_CLIENT_ENV === 'development' &&
      MarkdownLinkHelper.DEV_HOSTNAMES.includes(hostname)
    )
      return true;
    return (
      hostname === MarkdownLinkHelper.FIRST_PARTY_DOMAIN ||
      hostname.endsWith(`.${MarkdownLinkHelper.FIRST_PARTY_DOMAIN}`)
    );
  }

  private static getExtension(href: string): string {
    const pathname = MarkdownLinkHelper.getPathname(href);
    const lastSegment = pathname.split('/').pop() ?? '';
    if (!lastSegment.includes('.')) return '';
    return lastSegment.split('.').pop()?.toLowerCase() ?? '';
  }

  static classify(href: string): MarkdownMediaKind {
    const extension = MarkdownLinkHelper.getExtension(href);
    if (MarkdownLinkHelper.IMAGE_EXTENSIONS.includes(extension)) return 'image';
    if (extension === 'pdf') return 'pdf';
    return 'other';
  }

  /**
   * The in-app destination behind a same-origin href: the path to push, plus the client the agent named.
   *
   * Record links arrive as `/corporation/123?companyId=456`, so the client is part of the destination.
   */
  static toInternalTarget(
    href: string,
    currentOrigin: string
  ): IMarkdownLinkTarget | null {
    let url: URL;
    try {
      url = new URL(href, currentOrigin);
    } catch {
      return null;
    }

    // API upload pages and other Commenda hosts must keep their original origin.
    if (url.origin !== currentOrigin) return null;

    const rawCompanyId = url.searchParams.get('companyId');
    const companyId = rawCompanyId === null ? null : Number(rawCompanyId);

    return {
      path: `${url.pathname}${url.search}${url.hash}`,
      companyId:
        companyId !== null && Number.isFinite(companyId) ? companyId : null,
    };
  }

  /** Human-readable file name from the URL path, decoded, falling back when the path has none. */
  static getFileName(href: string, fallback: string): string {
    const lastSegment = MarkdownLinkHelper.getPathname(href).split('/').pop();
    if (!lastSegment) return fallback;
    try {
      return decodeURIComponent(lastSegment) || fallback;
    } catch {
      return lastSegment;
    }
  }
}
